import classNames from "classnames";
import AppContext from "PFApp/app_context";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import React, { forwardRef, UIEvent, useContext, useMemo } from "react";

import { useCalendarContext } from "../context/calendar_context";
import {
  CONTROLS_HEIGHT,
  DAY_HEADER_HEIGHT,
  GANTT_MATCH_MONTHS_LIMIT,
  MONTH_HEADER_HEIGHT
} from "./calendar";
import css from "./calendar.module.scss";
import { CalendarHeader } from "./calendar_header/calendar_header";
import { DayDividers } from "./days";
import { Rows } from "./rows";

type CalendarViewProps = {
  headerRef: React.MutableRefObject<HTMLDivElement>;
  startDate: string;
  endDate: string;
  daysCount: number;
  offsetTop: number;
  onScroll: (e: UIEvent<HTMLDivElement>) => void;
};

const CalendarView = forwardRef<HTMLDivElement, CalendarViewProps>(
  ({ headerRef, startDate, endDate, offsetTop, onScroll }, ref) => {
    const {
      store: { currentAccount }
    } = useContext(AppContext);
    const { dayWidth, days } = useCalendarContext();

    const { utc } = useDateFormatter();

    const matchMonthsLimit = currentAccount.availability.match_months_limit || GANTT_MATCH_MONTHS_LIMIT;

    const offsetLeft = useMemo(() => {
      const date = utc(startDate);
      const matchMonthsLimitDate = utc()
        .add(matchMonthsLimit * 2, "months")
        .endOf("month");

      const offLimit = matchMonthsLimitDate.diff(date, "days");
      const daysLimit = Math.min(offLimit, days.length);
      return daysLimit * dayWidth;
    }, [startDate, matchMonthsLimit, days.length, dayWidth]);

    const offLimitStyle = useMemo(
      () => ({ width: days.length * dayWidth - offsetLeft, left: offsetLeft }),
      [dayWidth, days.length, offsetLeft]
    );

    const calendarViewTopOffset =
      CONTROLS_HEIGHT + MONTH_HEADER_HEIGHT + DAY_HEADER_HEIGHT + DAY_HEADER_HEIGHT;

    return (
      <>
        <CalendarHeader headerRef={headerRef} onScroll={onScroll} />
        <div
          style={{
            height: `calc(100% - 1px - ${calendarViewTopOffset}px)`
          }}
          className={classNames(css.scrollWrapper, css.hideScrollbar)}
          ref={ref}
          onScroll={onScroll}
        >
          <div className={css.calendarBackground} style={{ width: offsetLeft }}>
            <DayDividers />
            <div className={css.offLimit} style={offLimitStyle} />
          </div>
          <Rows startDate={startDate} endDate={endDate} offsetTop={offsetTop} width={offsetLeft} />
        </div>
      </>
    );
  }
);

CalendarView.displayName = "CalendarView";

export default CalendarView;
